import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

import { ApiProvitoolService } from '@core/apis/api-provitool.service';
import { ApiShivaService } from '@core/apis/api-shiva.service';
import { ScannedEquipmentListComponent } from '@core/components/scanned-equipment-list/scanned-equipment-list.component';
import { ScannedEquipmentManager } from '@core/components/scanned-equipment-list/scanned-equipment.manager';
import { EquipmentLocationEnum, FormMode } from '@core/constants';
import {
  ICatoNetworkDevice,
  IEquipment,
  IGenericListOptions,
  IMetadataScannedEquipment,
  IMiniEquipment,
  IPrintLabelItem_new,
  IPrintLabelObject_new,
  ITinyEquipment,
  ITinyEquipmentModel,
  IWorkOrderItems
} from '@core/interfaces';
import { PromisesService } from '@core/services/promises.service';
import { SignalsService } from '@core/services/signals.service';
import { EquipmentScanSearchService } from '@views/work-order-items/work-order-items-metadata-templates/equipment-scan-search.service';

interface IPcatoMetadata {
  model: ITinyEquipmentModel;
  network_device_code?: string;
  quantity: number;
  equipments: IMiniEquipment[];
}

@Component({
  selector: 'app-pcato-metadata',
  templateUrl: './pcato-metadata.component.html',
  styleUrls: ['../work-order-items-detail-metadata.component.less']
})
export class PcatoMetadataComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('f', { static: true }) public f: NgForm;
  @ViewChild('serialNumberInput') public serialNumberInput?: ElementRef | undefined;
  @ViewChild('scannedEquipmentList', { static: true }) public scannedEquipmentList: ScannedEquipmentListComponent;

  @Input() public mode: FormMode = 'normal';
  @Input() public woi: IWorkOrderItems;
  @Input() public woiSave: Function; // woi save method exposed to child metadata component

  public loading: boolean;
  public networkDeviceFieldOptions: IGenericListOptions;
  public networkDevice: ICatoNetworkDevice | undefined;
  public equipmentModel: ITinyEquipmentModel | undefined;
  public serialNumber: string;

  public allEquipmentScanned: boolean = false;
  public hasInvalidScannedEquipment: boolean = false;

  private metadataBackup: IPcatoMetadata;
  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly toastr: ToastrService,
    private readonly apiProvitool: ApiProvitoolService,
    private readonly apiShiva: ApiShivaService,
    private readonly promisesService: PromisesService,
    private readonly signalsService: SignalsService,
    private readonly scannedEquipmentManager: ScannedEquipmentManager,
    private readonly equipmentScanSearchService: EquipmentScanSearchService,
  ) {}

  public ngOnInit(): void {
    this.woi.metadata = this.woi.metadata || {} as IPcatoMetadata;

    this.subscriptions.push(this.signalsService.subscribe('woi-edition-cancelled', () => {
      this.woi.metadata = {
        ...this.woi.metadata,
        ...this.metadataBackup,
      };
      this._init();
    }));

    this.subscriptions.push(this.signalsService.subscribe('pcato-metadata-refresh', () => this._init()));

    this._init();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const previousMode = changes?.mode?.previousValue;
    const currentMode = changes?.mode?.currentValue;

    if (previousMode === 'normal' && currentMode === 'edition') {
      this.metadataBackup = JSON.parse(JSON.stringify(this.woi.metadata));
      setTimeout(() => this.serialNumberInput?.nativeElement.focus());
    }

    if (previousMode === 'edition' && currentMode === 'normal') {
      this._checkScannedEquipmentValidity();
    }

    // To display the network device in the form when the woi state changes from check-network-in_progress to ready
    this._init();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  private _init(): void {
    this.networkDeviceFieldOptions = {
      filters: {
        type: 'cato',
        entity__code: this.woi.work_order.entity.code,
        work_order_item__isnull: true,
        is_active: true,
      },
      disabledButtons: {
        create: true
      }
    };

    this._fetchNetworkDevice(this.woi?.metadata?.network_device_code);

    if (['in-progress'].includes(this.woi?.state?.name)) {
      this._checkScannedEquipmentValidity();
    }
    this.buildPrintData = this.buildPrintData.bind(this);
  }

  // --------
  // Network device stuff
  // --------

  public onNetworkDeviceChange(networkDevice: ICatoNetworkDevice | undefined): void {
    if (!networkDevice) {
      // If no network device, then remove info from metadata
      this.networkDevice = undefined;
      delete this.woi.metadata.network_device_code;
    } else {
      // Otherwise make sure everything is up to date so that the display happens correctly
      this.networkDevice = networkDevice;
      this.woi.metadata.network_device_code = networkDevice.code;
    }
    this.woi.metadata.quantity = this._getQuantityFromNetworkDevice(networkDevice);
  }

  /**
   * Load the network device from the code saved in the metadata.
   * If the code i snot provided, set the network device to undefined.
   * @param networkDeviceCode The network device to load
   */
  private _fetchNetworkDevice(networkDeviceCode: string | undefined): void {
    if (!networkDeviceCode) {
      this.onNetworkDeviceChange(undefined);
      return;
    }
    this.apiProvitool.network_devices.detail(networkDeviceCode)
      .then((networkDevice: ICatoNetworkDevice) => {
        this.onNetworkDeviceChange(networkDevice);
      })
      .catch((err: unknown) => {
        console.error(err);
        this.toastr.error(`Impossible de récupérer les informations liées au modèle ${networkDeviceCode}. Veuillez rafraîchir votre page.`);
      });
  }

  /**
   * Calculate the quantity to scan from the selected network device : nothing => 0, non-cluster => 1, cluster => 2
   * @param networkDevice The network device, if selected
   * @returns the quantity to scan
   * @private
   */
  private _getQuantityFromNetworkDevice(networkDevice?: ICatoNetworkDevice | undefined): number {
    if (!networkDevice) {
      return 0;
    }
    return networkDevice.is_nat_cluster ? 2 : 1;
  }

  // --------
  // Scanning stuff
  // --------

  public searchForSerial(): void {
    this.f.controls.serialNumber.markAsTouched();
    this.loading = true;
    this.equipmentScanSearchService.searchBySerial(this.serialNumber, this.woi.metadata.equipments || [])
      .then((equipment: IEquipment) => {
        this.serialNumber = null;
        return this._addScannedEquipment(equipment);
      })
      .catch((message: string | undefined) => {
        if (message) {
          this.toastr.error(message, 'Recherche Equipement');
        }
      })
      .finally(() => this.loading = false);
  }

  public updateScannedEquipmentMetadata(updatedEquipmentMetadata: IMetadataScannedEquipment[]): void {
    this.woi.metadata.equipments = [...updatedEquipmentMetadata];
    this._updateScannedEquipment();
  }

  /**
   * Return the equipment to the stock if it is marked as ignored in the scanned equipment list
   * @param equipmentCode The ignored equipment code
   */
  public async updateEquipmentLocation(equipmentCode: string): Promise<void> {
    if (!equipmentCode) {
      return;
    }
    const equipment: IEquipment = await this.apiShiva.equipments.detail(equipmentCode);
    if (equipment.location !== EquipmentLocationEnum.Collect) {
      return;
    }
    equipment.location = EquipmentLocationEnum.Stock;
    await this.apiShiva.equipments.update(equipment.id, equipment);
  }

  private _addScannedEquipment(equipment: ITinyEquipment): void {
    const newScannedEquipments: IMetadataScannedEquipment[] = this.scannedEquipmentManager.addScannedEquipment(
      equipment,
      this.woi.metadata.equipments,
      this.woi.metadata.requested_equipment,
      this.woi,
      this.networkDevice.equipment_model,
      this.scannedEquipmentManager.formatNetworkDeviceForMetadata(this.networkDevice),
    );
    // Add the scanned equipment metadata to the work order items metadata
    this.woi.metadata.equipments = [...newScannedEquipments];
    this._updateScannedEquipment();
  }

  private _updateScannedEquipment(): void {
    // Update the view to reflect the new values
    this._checkScannedEquipmentValidity();

    // Make sure we update the work order item, in case the user refreshes the page or something
    this.woiSave();

    // Select the serial number input again for the next equipment
    setTimeout(() => this.serialNumberInput?.nativeElement.focus());
  }

  /**
   * Check if any equipment was scanned and if any items are invalid. Updates the component variables.
   * @private
   */
  private _checkScannedEquipmentValidity(): void {
    const { allScanned, hasErrors } = this.scannedEquipmentManager.checkScanStatus(this.woi.metadata.quantity, this.woi.metadata.equipments);
    this.allEquipmentScanned = allScanned;
    this.hasInvalidScannedEquipment = hasErrors;
  }

  // --------
  // Print stuff
  // --------

  public buildPrintData(currentEqp: IMetadataScannedEquipment = null): Promise<IPrintLabelObject_new> {
    const deferred = this.promisesService.defer();

    const parentName = this.woi?.work_order?.entity?.parent?.name;
    const entityName = this.woi?.work_order?.entity?.name;
    const customerRef = this.woi?.work_order?.entity?.customer_ref;
    const locationCity = this.woi?.location?.city;

    const printData = {
      woi_code: this.woi.code || '',
      quantity: this.woi?.metadata?.quantity || 1 ,
      equipments_codes: [],
      labels_data: {
        entity_code: this.woi?.work_order?.entity?.code || '',
        parent_name: parentName || entityName || '',
        customer_ref: customerRef || locationCity || '',
        labels: this._getPrintDataEqpObj(currentEqp),
        lr_code: this.woi?.logistics_request?.code || '',
        company_name: this.woi.work_order?.entity?.parent?.name || '',
      }
    };
    deferred.resolve(printData);

    return deferred.promise;
  }

  private _getEquipmentName(equipment: IMetadataScannedEquipment, eqpList: IMetadataScannedEquipment[]): string {
    let equipment_name = equipment?.network_device?.name || '';
    if (equipment && (eqpList.length > 1 && equipment.code === eqpList[1].code)) {
      equipment_name = equipment?.network_device?.spare_name || '';
    }
    return equipment_name;
  }

  private _getPrintDataEqpObj(currentEqp: IMetadataScannedEquipment): IPrintLabelItem_new[] {
    const printLabelItem: IPrintLabelItem_new[] = [];
    const eqpList = this.woi?.metadata?.equipments;
    if (!currentEqp && eqpList && eqpList.length) {
      eqpList.forEach((eqp: IMetadataScannedEquipment)=> {
        const equipment_name = this._getEquipmentName(eqp, eqpList);
        printLabelItem.push({
          equipment_name: equipment_name,
        });
      });
    } else {
      const equipment_name = this._getEquipmentName(currentEqp, eqpList);
      printLabelItem.push({
        equipment_name: equipment_name,
      });
    }

    return printLabelItem;
  }
}
